import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./Assessment.module.css";
import api from "../../api";
import { assessmentParam, inAssessment, withAssessment } from "./assessmentFilter";

// A question's tile: final answers are "Submitted"; subjective drafts and
// programming attempts that aren't final yet say so.
const stateOf = (p, type) => {
  const done = p.submitted && (type === "o" || p.final !== false);
  const started = p.submitted && !done;
  return {
    state: done ? "Submitted" : started ? (type === "p" ? "Attempted · not final" : "Draft saved") : "Not started",
    stateFg: done ? "#1f8a4c" : started ? "#b07d1a" : "#9db1bf",
    dotColor: done ? "#1f9d4d" : started ? "#e0a52c" : "#dbe3ea",
    borderColor: done ? "#c9e9d6" : started ? "#f1dcae" : "#e4eaf0",
  };
};

const QuestionTypePicker = () => {
  const { id, topicId } = useParams();
  const assessment = assessmentParam(useLocation().search);
  const navigate = useNavigate();
  const selectedModuleId = useSelector((state) => state.module.selectedModuleId);
  const effectiveTopicId = topicId || selectedModuleId;
  const courseData = useSelector((state) => state.module.selectedModuleData);

  const [loading, setLoading] = useState(true);
  const [problemTypes, setProblemTypes] = useState([]);
  const [typeCounts, setTypeCounts] = useState({ p: 0, s: 0, o: 0 });
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveTopicId) return;
      setLoading(true);
      try {
        // Fetch available types first
        const typesRes = await api.get(`course/available-problem-types/${effectiveTopicId}/`);
        const types = typesRes.data || [];
        setProblemTypes(types);

        // Fetch questions for each available type
        const allQuestions = [];
        const counts = { p: 0, s: 0, o: 0 };

        // Fetch programming problems if available
        if (types.includes("p")) {
          try {
            const progRes = await api.get(`course/${effectiveTopicId}/p/`);
            const progData = progRes.data || [];
            counts.p = progData.length;
            progData.forEach((p, i) => {
              allQuestions.push({
                id: p.id,
                type: "p",
                number: allQuestions.length + 1,
                label: p.description?.substring(0, 35) || `Programming Q${i + 1}`,
                ...stateOf(p, "p"),
                assessment: p.assessment || null,
                maxScore: p.max_score,
              });
            });
          } catch (e) { console.log("No programming problems"); }
        }

        // Fetch subjective problems if available
        if (types.includes("s")) {
          try {
            const subjRes = await api.get(`course/${effectiveTopicId}/s/`);
            const subjData = subjRes.data || [];
            counts.s = subjData.length;
            subjData.forEach((p, i) => {
              allQuestions.push({
                id: p.id,
                type: "s",
                number: allQuestions.length + 1,
                label: p.description?.substring(0, 35) || `Subjective Q${i + 1}`,
                ...stateOf(p, "s"),
                assessment: p.assessment || null,
                maxScore: p.max_score,
              });
            });
          } catch (e) { console.log("No subjective problems"); }
        }

        // Fetch MCQ problems if available
        if (types.includes("o")) {
          try {
            const mcqRes = await api.get(`course/${effectiveTopicId}/o/`);
            const mcqData = mcqRes.data || [];
            counts.o = mcqData.length;
            mcqData.forEach((p, i) => {
              allQuestions.push({
                id: p.id,
                type: "o",
                number: allQuestions.length + 1,
                label: p.description?.substring(0, 35) || `MCQ Q${i + 1}`,
                ...stateOf(p, "o"),
                assessment: p.assessment || null,
                maxScore: p.max_score,
              });
            });
          } catch (e) { console.log("No MCQ problems"); }
        }

        // Opened from one assessment on the module card: only its questions.
        const shown = allQuestions.filter((q) => inAssessment(q, assessment)).map((q, i) => ({ ...q, number: i + 1 }));
        const shownCounts = { p: 0, s: 0, o: 0 };
        shown.forEach((q) => { shownCounts[q.type] += 1; });
        if (assessment) {
          setProblemTypes(types.filter((t) => shownCounts[t] > 0));
          setTypeCounts(shownCounts);
        } else {
          setTypeCounts(counts);
        }
        setQuestions(shown);
      } catch (err) {
        console.error("Failed to fetch assessment data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [effectiveTopicId, assessment]);

  const handleBack = () => {
    navigate(`/dashboard/workspace/${id}`);
  };

  const handleTypeSelect = (type) => {
    // type is short code: p, s, o
    const firstProblem = questions.find((q) => q.type === type);
    if (firstProblem) {
      navigate(withAssessment(`/dashboard/workspace/${id}/assessment/${effectiveTopicId}/${type}/${firstProblem.id}`, assessment));
    }
  };

  // Mentors upload a module's questions as several named assessments; show
  // each as its own set. Questions from before assessments come last.
  const assessmentGroups = React.useMemo(() => {
    if (!questions.some((q) => q.assessment)) return [];
    const groups = new Map();
    questions.forEach((q) => {
      const key = q.assessment ? q.assessment.id : "other";
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          title: q.assessment ? q.assessment.title : "Other questions",
          order: q.assessment ? q.assessment.order : Infinity,
          questions: [],
        });
      }
      groups.get(key).questions.push(q);
    });
    return [...groups.values()].sort((a, b) => a.order - b.order);
  }, [questions]);

  const typeShort = { p: "Programming", s: "Subjective", o: "MCQ" };

  // Points per question of a type, from the questions themselves.
  const pointsLabel = (type) => {
    const scores = questions.filter((q) => q.type === type).map((q) => Number(q.maxScore) || 0);
    if (!scores.length) return "";
    const fmt = (n) => Number(n.toFixed(1));
    const lo = Math.min(...scores);
    const hi = Math.max(...scores);
    const total = fmt(scores.reduce((a, b) => a + b, 0));
    return lo === hi ? `${fmt(lo)} pts each · ${total} total` : `${fmt(lo)}–${fmt(hi)} pts · ${total} total`;
  };

  const handleQuestionClick = (question) => {
    navigate(withAssessment(`/dashboard/workspace/${id}/assessment/${effectiveTopicId}/${question.type}/${question.id}`, assessment));
  };

  // API returns short codes: p = programming, s = subjective, o = MCQ (objective)
  const typeConfig = {
    p: {
      key: "programming",
      glyph: "{ }",
      title: "Programming",
      desc: "Write and run code against test cases in a sandboxed compiler.",
      iconBg: "#e3f0f9",
      iconFg: "#0f6ca8",
      borderColor: "#e4eaf0",
      shadow: "none",
    },
    s: {
      key: "subjective",
      glyph: "¶",
      title: "Subjective",
      desc: "Long-form written answers reviewed by your mentor.",
      iconBg: "#f0ecfa",
      iconFg: "#6b4fb5",
      borderColor: "#e4eaf0",
      shadow: "none",
    },
    o: {
      key: "mcq",
      glyph: "◉",
      title: "Multiple Choice",
      desc: "Single or multi-select questions, graded instantly.",
      iconBg: "#e6f6ec",
      iconFg: "#1f8a4c",
      borderColor: "#e4eaf0",
      shadow: "none",
    },
  };

  if (loading) {
    return (
      <div className={styles.pickerContainer}>
        <div className={styles.pickerLoading}>Loading assessment...</div>
      </div>
    );
  }

  return (
    <div className={styles.pickerContainer}>
      {/* Header */}
      <div className={styles.pickerHeader}>
        <div className={styles.pickerHeaderLeft}>
          <button className={styles.backButton} onClick={handleBack}>
            ←
          </button>
          <span className={styles.headerTitle}>
            {assessment ? questions[0]?.assessment?.title || "Other questions" : "Assessment"}
          </span>
          <div className={styles.headerDivider} />
          <span className={styles.headerSubtitle}>
            {courseData?.name || "Module"} · Concept Building
          </span>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pickerContent}>
        <div className={styles.pickerIntro}>
          <h1 className={styles.pickerTitle}>What would you like to attempt?</h1>
          <p className={styles.pickerDescription}>
            {questions.length} questions in this set. You can switch types at any time — progress is kept per question.
            {assessment && (
              <>
                {" "}
                <button type="button" onClick={() => navigate(`/dashboard/workspace/${id}/assessment/${effectiveTopicId}`)}
                        style={{ background: "none", border: "none", padding: 0, color: "#0f6ca8", cursor: "pointer",
                                 font: "inherit", textDecoration: "underline" }}>
                  See all of this module's questions
                </button>
              </>
            )}
          </p>
        </div>

        {/* Type Cards */}
        <div className={styles.typeCards}>
          {problemTypes.map((type) => {
            const config = typeConfig[type];
            if (!config) return null; // Skip unknown types
            const count = typeCounts[type] || 0;
            return (
              <div
                key={type}
                className={styles.typeCard}
                style={{
                  borderColor: config.borderColor,
                  boxShadow: config.shadow,
                }}
                onClick={() => handleTypeSelect(type)}
              >
                <div className={styles.typeCardHeader}>
                  <div
                    className={styles.typeIcon}
                    style={{ background: config.iconBg, color: config.iconFg }}
                  >
                    {config.glyph}
                  </div>
                  <div
                    className={styles.typeCount}
                    style={{ background: config.iconBg, color: config.iconFg }}
                  >
                    {count} {count === 1 ? "question" : "questions"}
                  </div>
                </div>
                <h3 className={styles.typeTitle}>{config.title}</h3>
                <p className={styles.typeDesc}>{config.desc}</p>
                <div className={styles.typeMeta}>
                  <span>~{type === "p" ? count * 12 : type === "s" ? count * 8 : count * 3} min</span>
                  <span>·</span>
                  <span>{pointsLabel(type)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* One set per assessment */}
        {assessmentGroups.map((group) => {
          const done = group.questions.filter((q) => q.state === "Submitted").length;
          return (
            <div key={group.key} className={styles.questionStrip} data-testid="assessment-group">
              <div className={styles.stripLabel}>
                {group.title} · {group.questions.length} {group.questions.length === 1 ? "question" : "questions"}
                {done > 0 ? ` · ${done} submitted` : ""}
              </div>
              <div className={styles.stripItems} style={{ flexWrap: "wrap" }}>
                {group.questions.map((q, i) => (
                  <div
                    key={`${q.type}-${q.id}`}
                    className={styles.stripItem}
                    style={{ borderColor: q.borderColor }}
                    onClick={() => handleQuestionClick(q)}
                  >
                    <div className={styles.stripItemHeader}>
                      <span className={styles.stripItemNumber}>Q{i + 1} · {typeShort[q.type]}</span>
                      <div className={styles.stripItemDot} style={{ background: q.dotColor }} />
                    </div>
                    <div className={styles.stripItemLabel}>{q.label}</div>
                    <div className={styles.stripItemState} style={{ color: q.stateFg }}>
                      {q.state}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Question Strip */}
        {questions.length > 0 && assessmentGroups.length === 0 && (
          <div className={styles.questionStrip}>
            <div className={styles.stripLabel}>Questions in this set</div>
            <div className={styles.stripItems}>
              {questions.slice(0, 5).map((q) => (
                <div
                  key={`${q.type}-${q.id}`}
                  className={styles.stripItem}
                  style={{ borderColor: q.borderColor }}
                  onClick={() => handleQuestionClick(q)}
                >
                  <div className={styles.stripItemHeader}>
                    <span className={styles.stripItemNumber}>Q{q.number}</span>
                    <div
                      className={styles.stripItemDot}
                      style={{ background: q.dotColor }}
                    />
                  </div>
                  <div className={styles.stripItemLabel}>{q.label}</div>
                  <div
                    className={styles.stripItemState}
                    style={{ color: q.stateFg }}
                  >
                    {q.state}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionTypePicker;
